/* ==========================================================================
   MIDOLO.ART — Photo Gallery
   Vanilla, dependency-free, defensive. Enhances markup that already works.
   Flagship layer: custom cursor, FLIP filtering, shared-element lightbox
   transition, neighbor preload, click-to-zoom (desktop), drag-to-dismiss
   (touch), swipe, full keyboard + focus trap. Honors prefers-reduced-motion.
   ========================================================================== */
(function () {
  "use strict";

  var EASE = "cubic-bezier(0.16, 1, 0.3, 1)";
  var REDUCED = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  var FINE = window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
  var FOCUSABLE = 'a[href],button:not([disabled]),[tabindex]:not([tabindex="-1"])';
  var COSTA_RICA_ALBUM = "costa-rica-2026";

  function updateResponsiveImageSizes(root, items, filter) {
    var mosaic = root.classList.contains("mg-view-mosaic");
    var editorial = filter === COSTA_RICA_ALBUM && !mosaic;
    items.forEach(function (item) {
      var image = item.querySelector("[data-mg-sizes-mosaic]");
      if (!image) return;
      var attribute = mosaic ? "data-mg-sizes-mosaic" : "data-mg-sizes-default";
      if (editorial) {
        var editorialAttribute = item.classList.contains("mg-card--album-featured")
          ? "data-mg-sizes-editorial-featured"
          : "data-mg-sizes-editorial";
        if (image.getAttribute(editorialAttribute)) attribute = editorialAttribute;
      }
      var nextSizes = image.getAttribute(attribute);
      if (nextSizes && image.sizes !== nextSizes) image.sizes = nextSizes;
    });
  }

  function updateVisiblePhotoLabels(items) {
    var visible = items.filter(function (item) { return !item.hidden; });
    var total = visible.length;
    visible.forEach(function (item, index) {
      var trigger = item.querySelector("[data-mg-open]");
      if (!trigger) return;
      var title = (trigger.getAttribute("data-title") || "").trim();
      var album = (trigger.getAttribute("data-category") || "").trim() || "Galleria";
      var position = index + 1;
      var label = title
        ? "Apri " + title + " — foto " + position + " di " + total + " — " + album
        : "Apri foto " + position + " di " + total + " — " + album;
      trigger.setAttribute("aria-label", label);
    });
  }

  function updateAlbumLayout(root, items, filter) {
    root.setAttribute("data-active-album", filter);
    items.forEach(function (item) { item.classList.remove("mg-card--album-featured"); });
    if (filter === COSTA_RICA_ALBUM) {
      for (var i = 0; i < items.length; i++) {
        if (!items[i].hidden) {
          items[i].classList.add("mg-card--album-featured");
          break;
        }
      }
    }
    updateResponsiveImageSizes(root, items, filter);
    updateVisiblePhotoLabels(items);
  }

  function shouldReplayViewReveal(view, animate) {
    return Boolean(animate && view !== "mosaic");
  }

  if (typeof module !== "undefined" && module.exports) {
    module.exports.updateAlbumLayout = updateAlbumLayout;
    module.exports.updateResponsiveImageSizes = updateResponsiveImageSizes;
    module.exports.updateVisiblePhotoLabels = updateVisiblePhotoLabels;
    module.exports.shouldReplayViewReveal = shouldReplayViewReveal;
    module.exports.computeFlowDelays = computeFlowDelays;
    module.exports.replayReveal = replayReveal;
  }

  /* ---------- Share (Web Share API → clipboard fallback → toast) ----------- */
  function mgToast(msg) {
    var t = document.querySelector("[data-mg-toast]");
    if (!t) return;
    t.textContent = msg;
    t.classList.add("is-on");
    clearTimeout(t.__mgT);
    t.__mgT = setTimeout(function () { t.classList.remove("is-on"); }, 2200);
  }
  function mgShare(url, title) {
    if (navigator.share) {
      navigator.share({ title: title || document.title, url: url }).catch(function () {});
    } else if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(url).then(function () { mgToast("Link copiato ✓"); }, function () { mgToast(url); });
    } else {
      mgToast(url);
    }
  }

  /* ---------- Reveal replay: recompute flow delays, re-materialise from red - */
  function computeFlowDelays(root) {
    var grid = root.querySelector("[data-mg-grid]");
    var gw = (grid && grid.offsetWidth) || 1;
    var cards = Array.prototype.slice.call(root.querySelectorAll(".mg-card")).filter(function (card) {
      return !card.hidden;
    });
    // Read ALL geometry first, then write — interleaving offset reads with style writes forced
    // one layout reflow per card (up to 81) at init/replay. Batching kills that thrash.
    var pos = [];
    for (var r = 0; r < cards.length; r++) pos.push(cards[r].offsetLeft, cards[r].offsetTop);
    for (var i = 0; i < cards.length; i++) {
      var nx = pos[i * 2] / gw;
      var fieldv = Math.sin((nx * 2.2 + pos[i * 2 + 1] * 0.004) * Math.PI) * 0.5 + 0.5;
      var jitter = ((i * 9301 + 49297) % 233280) / 233280;
      cards[i].style.setProperty("--mg-reveal-delay", Math.round(fieldv * 360 + jitter * 180) + "ms");
    }
  }
  function replayReveal(root) {
    if (REDUCED || root.getAttribute("data-reveal") !== "true" || !root.classList.contains("mg-enh") || root.classList.contains("mg-view-mosaic")) return;
    computeFlowDelays(root);
    var cards = Array.prototype.slice.call(root.querySelectorAll(".mg-card")).filter(function (c) { return !c.hidden; });
    if (!cards.length) return;
    // Snap every visible card back under its red cover, with no transition.
    cards.forEach(function (c) { c.classList.add("mg-replay"); c.classList.remove("is-in"); });
    // Commit that "red" state as the transition start value BEFORE releasing.
    // A bare offsetWidth reflow only flushes layout and can skip opacity (a
    // composite-only property), so the dissolve fired only intermittently
    // (esp. in Safari). Reading the computed opacity of a real reveal cover
    // forces a style flush that includes opacity, so the replay always fires.
    var probe = cards[0].querySelector(".mg-card__reveal") || cards[0];
    void getComputedStyle(probe).opacity;
    void root.offsetWidth;
    // Release: the red dissolves again on the freshly computed staggered delays.
    cards.forEach(function (c) { c.classList.remove("mg-replay"); c.classList.add("is-in"); });
  }

  function init(root) {
    if (!root || root.__mgInit) return;
    root.__mgInit = true;
    numberPerAlbum(root);
    setupLQIP(root);
    setupFilters(root);
    setupViewToggle(root);
    setupReveal(root);
    updateResponsiveImageSizes(
      root,
      Array.prototype.slice.call(root.querySelectorAll("[data-mg-item]")),
      root.getAttribute("data-active-album") || "*"
    );
    setupKeyboard(root);
    setupCursor(root);
    setupLightbox(root);
    setupProtection(root);
    setupBackToTop(root);
  }

  /* ---------- Blur-up: fade each image in over its tiny LQIP backdrop ------ */
  function setupLQIP(root) {
    root.classList.add("mg-enh");
    root.querySelectorAll(".mg-card__img").forEach(function (img) {
      var card = img.closest(".mg-card") || img;
      function done() { card.classList.add("is-loaded"); }
      if (img.complete && img.naturalWidth) { done(); return; }
      img.addEventListener("load", done, { once: true });
      img.addEventListener("error", done, { once: true });
    });
  }

  /* ---------- Per-album numbering: each album counts its photos 01..N ------ */
  function numberPerAlbum(root) {
    var items = root.querySelectorAll("[data-mg-item]");
    if (!items.length) return;
    var counts = {};
    items.forEach(function (item) {
      var badge = item.querySelector(".mg-card__index");
      if (!badge) return;
      var cats = (item.getAttribute("data-category") || "").split(/\s+/).filter(Boolean);
      var album = cats.length ? cats[0] : "_all";
      counts[album] = (counts[album] || 0) + 1;
      var n = counts[album];
      badge.textContent = n < 10 ? "0" + n : "" + n;
    });
  }

  /* ---------- Keyboard shortcuts: G = galleria/mosaico · / = focus filtri -- */
  function setupKeyboard(root) {
    document.addEventListener("keydown", function (e) {
      var lb = root.querySelector("[data-mg-lightbox]");
      if (lb && !lb.hidden) return; // the lightbox has its own keys when open
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      var tag = e.target && e.target.tagName;
      if (tag && /^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      var k = (e.key || "").toLowerCase();
      if (k === "g") {
        var tg = root.querySelector("[data-mg-view-toggle]");
        var inactive = tg && tg.querySelector(".mg-viewbtn:not(.is-active)");
        if (inactive) { e.preventDefault(); inactive.click(); }
      } else if (k === "/") {
        var f = root.querySelector(".mg-filter");
        if (f) { e.preventDefault(); f.focus(); }
      }
    });
  }

  /* ---------- Image protection (deterrent: not real DRM) ------------------ */
  function setupProtection(root) {
    if (root.getAttribute("data-protect") !== "true") return;
    root.classList.add("mg-protect");
    root.addEventListener("contextmenu", function (e) {
      if (e.target.closest("img, .mg-card__media, .mg-lightbox__stage")) e.preventDefault();
    });
    root.addEventListener("dragstart", function (e) {
      if (e.target.closest("img")) e.preventDefault();
    });
  }

  /* ---------- Scroll reveal ------------------------------------------------ */
  function setupReveal(root) {
    var items = Array.prototype.slice.call(root.querySelectorAll("[data-mg-reveal]"));
    if (!items.length) return;
    var revealAll = function () { items.forEach(function (el) { el.classList.add("is-in"); }); };

    // Flow-field stagger: organic per-card delays so the grid materialises like
    // particles emerging from a field. Recomputed on every replay.
    if (!root.classList.contains("mg-view-mosaic")) computeFlowDelays(root);

    if (REDUCED || root.getAttribute("data-reveal") !== "true" || !("IntersectionObserver" in window)) {
      revealAll();
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add("is-in");
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: "0px 0px -8% 0px", threshold: 0.08 });

    var vh = window.innerHeight || document.documentElement.clientHeight || 0;
    items.forEach(function (el) {
      // Above-the-fold items reveal immediately (don't depend on IO firing,
      // which is throttled in background tabs); the rest animate on scroll.
      if (el.getBoundingClientRect().top < vh) el.classList.add("is-in");
      else io.observe(el);
    });

    // Safety net: never leave content invisible if IO never fires.
    setTimeout(revealAll, 2500);
  }

  /* ---------- Filters (with FLIP) ----------------------------------------- */
  function setupFilters(root) {
    var bar = root.querySelector("[data-mg-filters]");
    if (!bar) return;
    var grid = root.querySelector("[data-mg-grid]");
    var items = Array.prototype.slice.call(root.querySelectorAll("[data-mg-item]"));
    if (!grid || !items.length) return;
    var intros = root.querySelector("[data-mg-album-intros]");

    bar.hidden = false; // progressive enhancement: only when JS is alive

    // --- Counts: grand total + per-album, on the pills and the live readout ---
    var total = items.length;
    function countFor(filter) {
      if (filter === "*") return total;
      return items.filter(function (it) {
        return (it.getAttribute("data-category") || "").split(/\s+/).indexOf(filter) !== -1;
      }).length;
    }
    bar.querySelectorAll(".mg-filter").forEach(function (b) {
      var badge = document.createElement("span");
      badge.className = "mg-filter__count";
      badge.textContent = countFor(b.getAttribute("data-filter"));
      b.appendChild(badge);
    });
    var countNum = root.querySelector("[data-mg-count-num]");
    var countLabel = root.querySelector("[data-mg-count-label]");
    function updateCount(filter) {
      if (!countNum) return;
      countNum.textContent = countFor(filter);
      if (countLabel) countLabel.textContent = "immagini";
    }

    // Show the album intro matching the selected filter ('*' = Tutto)
    function updateIntro(filter) {
      if (!intros) return;
      var panels = intros.querySelectorAll("[data-mg-album]");
      var shown = null;
      panels.forEach(function (p) { if (p.getAttribute("data-album") === filter) shown = p; });
      panels.forEach(function (p) {
        if (p !== shown) { p.hidden = true; p.classList.remove("is-in"); }
      });
      if (shown) {
        shown.hidden = false;
        if (REDUCED) { shown.classList.add("is-in"); }
        else { shown.classList.remove("is-in"); void shown.offsetWidth; shown.classList.add("is-in"); }
      }
    }

    // Sliding "magic pill" indicator that animates to the active filter
    var indicator = document.createElement("span");
    indicator.className = "mg-filters__indicator";
    bar.appendChild(indicator);
    bar.classList.add("has-indicator");

    function moveIndicator(animate) {
      var active = bar.querySelector(".mg-filter.is-active");
      if (!active) { indicator.style.opacity = "0"; return; }
      // Position relative to the bar (its containing block) — robust to offsetParent
      var barRect = bar.getBoundingClientRect();
      var r = active.getBoundingClientRect();
      if (!animate) indicator.style.transition = "none";
      indicator.style.width = r.width + "px";
      indicator.style.height = r.height + "px";
      indicator.style.transform = "translate3d(" + (r.left - barRect.left) + "px," + (r.top - barRect.top) + "px,0)";
      indicator.style.opacity = "1";
      if (!animate) { void indicator.offsetWidth; indicator.style.transition = ""; }
    }
    requestAnimationFrame(function () { moveIndicator(false); });
    var rT;
    window.addEventListener("resize", function () {
      clearTimeout(rT); rT = setTimeout(function () { moveIndicator(false); }, 120);
    });

    // Initial album intro + count for the active filter
    var initActive = bar.querySelector(".mg-filter.is-active");
    var initFilter = initActive ? initActive.getAttribute("data-filter") : "*";
    updateIntro(initFilter);
    updateCount(initFilter);

    // A ?album=<handle> deep link overrides the default landing album.
    var qAlbum = null;
    try { qAlbum = new URLSearchParams(location.search).get("album"); } catch (e) {}

    // The newest album is active by default (server sorts newest→oldest), so
    // apply its visibility on load — unless a deep link picks another one.
    if (initFilter !== "*") {
      items.forEach(function (item) {
        var cats = (item.getAttribute("data-category") || "").split(/\s+/);
        item.hidden = cats.indexOf(initFilter) === -1;
      });
    }
    updateAlbumLayout(root, items, initFilter);

    // --- Shareable albums: keep the URL in sync with the selected album ---
    function albumUrl(filter) {
      var base = location.origin + location.pathname;
      return filter === "*" ? base : base + "?album=" + filter;
    }
    function setAlbumUrl(filter) {
      try { history.replaceState(history.state, "", albumUrl(filter)); } catch (e) {}
    }
    // Current viewing experience → encoded in shared links so the recipient opens the same way.
    function currentView() {
      if (document.documentElement.classList.contains("mmp-cinema-lock")) return "viaggio";
      return root.classList.contains("mg-view-mosaic") ? "grid" : "editorial";
    }
    function withView(url, view) {
      return url + (url.indexOf("?") === -1 ? "?" : "&") + "mg-view=" + (view || currentView());
    }
    function activeFilter() {
      var a = bar.querySelector(".mg-filter.is-active");
      return a ? a.getAttribute("data-filter") : "*";
    }
    var shareAlbumBtn = root.querySelector("[data-mg-share-album]");
    if (shareAlbumBtn) shareAlbumBtn.addEventListener("click", function () {
      var a = bar.querySelector(".mg-filter.is-active");
      var label = a ? ((a.querySelector(".mg-filter__label") || a).textContent || "").trim() : "Galleria";
      mgShare(withView(albumUrl(activeFilter())), "MIDOLO — " + (label || "Galleria"));
      replayReveal(root);
    });
    // The immersive overlay's share button asks the gallery to build the VIAGGIO link.
    document.addEventListener("mg:share-viaggio", function () {
      mgShare(withView(albumUrl(activeFilter()), "viaggio"), "MIDOLO — VIAGGIO");
    });
    // Shared VIAGGIO link → auto-start the immersive mode once the player is wired.
    (function () {
      var qv = null;
      try { qv = new URLSearchParams(location.search).get("mg-view"); } catch (e) {}
      if (qv === "viaggio") setTimeout(function () { var vb = document.querySelector("[data-mmp-cinema]"); if (vb) vb.click(); }, 1000);
    })();
    // Open the gallery already filtered when the URL carries ?album=<handle>
    if (qAlbum) {
      var qTarget = null;
      bar.querySelectorAll(".mg-filter").forEach(function (b) { if (b.getAttribute("data-filter") === qAlbum) qTarget = b; });
      if (qTarget && !qTarget.classList.contains("is-active")) setTimeout(function () { qTarget.click(); }, 30);
    }

    bar.addEventListener("click", function (e) {
      var btn = e.target.closest(".mg-filter");
      if (!btn) return;
      var filter = btn.getAttribute("data-filter");

      bar.querySelectorAll(".mg-filter").forEach(function (b) {
        var active = b === btn;
        b.classList.toggle("is-active", active);
        b.setAttribute("aria-pressed", active ? "true" : "false");
      });
      moveIndicator(true);
      updateIntro(filter);
      updateCount(filter);
      setAlbumUrl(filter);

      // Apply visibility (data-category may hold multiple album handles), then
      // replay the red→photo reveal so the new album materialises from red.
      items.forEach(function (item) {
        var cats = (item.getAttribute("data-category") || "").split(/\s+/);
        item.hidden = !(filter === "*" || cats.indexOf(filter) !== -1);
      });
      updateAlbumLayout(root, items, filter);
      replayReveal(root);
    });
  }

  /* ---------- View toggle: editorial <-> mosaic overview ------------------ */
  function setupViewToggle(root) {
    var toggle = root.querySelector("[data-mg-view-toggle]");
    if (!toggle) return;
    var grid = root.querySelector("[data-mg-grid]");
    if (!grid) return;
    var items = Array.prototype.slice.call(root.querySelectorAll("[data-mg-item]"));
    var btns = Array.prototype.slice.call(toggle.querySelectorAll("[data-view]"));
    if (!btns.length) return;
    toggle.hidden = false; // progressive enhancement
    var KEY = "mg-view:" + (root.className || "x").replace(/\s+/g, ".");

    function apply(view, animate) {
      var mosaic = view === "mosaic";
      root.classList.toggle("mg-view-mosaic", mosaic);
      updateResponsiveImageSizes(root, items, root.getAttribute("data-active-album") || "*");
      btns.forEach(function (b) {
        var on = b.getAttribute("data-view") === view;
        b.classList.toggle("is-active", on);
        b.setAttribute("aria-pressed", on ? "true" : "false");
      });
      // Grid is a stable contact sheet; Editoriale keeps the materialisation replay.
      if (shouldReplayViewReveal(view, animate)) replayReveal(root);
      try { localStorage.setItem(KEY, view); } catch (e) {}
    }

    var saved = null;
    try { saved = localStorage.getItem(KEY); } catch (e) {}
    if (saved === "mosaic") apply("mosaic", false);
    // A shared ?mg-view= link picks the experience (grid/editorial) — overrides the saved one.
    var qv = null;
    try { qv = new URLSearchParams(location.search).get("mg-view"); } catch (e) {}
    if (qv === "grid") apply("mosaic", false);
    else if (qv === "editorial") apply("editorial", false);

    toggle.addEventListener("click", function (e) {
      var b = e.target.closest("[data-view]");
      if (b) apply(b.getAttribute("data-view"), true);
    });
  }

  /* ---------- Custom cursor ----------------------------------------------- */
  function setupCursor(root) {
    var cursor = root.querySelector("[data-mg-cursor]");
    if (!cursor) return;
    if (REDUCED || !FINE || root.getAttribute("data-cursor") !== "true") { cursor.remove(); return; }

    root.classList.add("mg-has-cursor");
    var x = 0, y = 0, cx = 0, cy = 0, running = false;

    function loop() {
      cx += (x - cx) * 0.22;
      cy += (y - cy) * 0.22;
      cursor.style.setProperty("--mg-x", cx.toFixed(2) + "px");
      cursor.style.setProperty("--mg-y", cy.toFixed(2) + "px");
      if (Math.abs(x - cx) > 0.1 || Math.abs(y - cy) > 0.1) {
        requestAnimationFrame(loop);
      } else { running = false; }
    }
    document.addEventListener("mousemove", function (e) {
      x = e.clientX; y = e.clientY;
      if (!running) { running = true; requestAnimationFrame(loop); }
    });

    root.querySelectorAll("[data-mg-open]").forEach(function (t) {
      t.addEventListener("mouseenter", function () { cursor.classList.add("is-active"); });
      t.addEventListener("mouseleave", function () { cursor.classList.remove("is-active"); });
    });
  }

  /* ---------- Lightbox ---------------------------------------------------- */
  function setupLightbox(root) {
    if (root.getAttribute("data-lightbox") !== "true") return;
    var box = root.querySelector("[data-mg-lightbox]");
    if (!box) return;
    var triggers = Array.prototype.slice.call(root.querySelectorAll("[data-mg-open]"));
    if (!triggers.length) return;

    var N = triggers.length;
    var img = box.querySelector("[data-mg-image]");
    var loader = box.querySelector("[data-mg-loader]");
    var frame = box.querySelector(".mg-lightbox__frame");
    var backdrop = box.querySelector(".mg-lightbox__backdrop");
    var elTitle = box.querySelector("[data-mg-title]");
    var elTag = box.querySelector("[data-mg-tag]");
    var elPlace = box.querySelector("[data-mg-place]");
    var elDesc = box.querySelector("[data-mg-desc]");
    var elCta = box.querySelector("[data-mg-cta]");
    var elCounter = box.querySelector("[data-mg-counter]");
    var elProgress = box.querySelector("[data-mg-progress]");

    var current = -1;
    var lastFocused = null;
    var lbOpen = false;
    var historyPushed = false;

    function data(i, key) { return triggers[i].getAttribute("data-" + key) || ""; }
    function setText(el, v) { if (el) el.textContent = v || ""; }

    function setMeta(i) {
      setText(elTitle, data(i, "title"));
      setText(elTag, data(i, "category"));
      setText(elPlace, data(i, "meta"));
      setText(elDesc, data(i, "description"));
      var href = data(i, "href");
      if (href) {
        elCta.hidden = false; elCta.href = href;
        elCta.textContent = data(i, "link-label") || "Scopri";
      } else { elCta.hidden = true; elCta.removeAttribute("href"); }
      var list = activeList();
      var pos = list.indexOf(i); if (pos < 0) pos = 0;
      var count = list.length;
      if (elCounter) elCounter.textContent = (pos + 1) + " / " + count;
      if (elProgress) elProgress.style.width = (((pos + 1) / count) * 100) + "%";
    }

    // The lightbox navigates only within the CURRENTLY VISIBLE items (active album).
    function itemHidden(i) {
      var it = triggers[i].closest("[data-mg-item]");
      return it ? it.hidden : false;
    }
    function activeList() {
      var arr = [];
      for (var k = 0; k < N; k++) { if (!itemHidden(k)) arr.push(k); }
      return arr.length ? arr : [current >= 0 ? current : 0];
    }

    function preload(k) {
      if (k < 0 || k >= N) return;
      var ss = data(k, "srcset"), full = data(k, "full");
      var p = new Image();
      if (ss) { p.srcset = ss; p.sizes = "100vw"; }
      p.src = full;
    }

    function showFull(i, fade, dir) {
      var ss = data(i, "srcset"), full = data(i, "full");
      if (loader) loader.hidden = false;
      if (fade) img.classList.remove("is-loaded");
      var pre = new Image();
      pre.onload = function () {
        if (current !== i) return;
        if (ss) { img.srcset = ss; img.sizes = "100vw"; } else { img.removeAttribute("srcset"); }
        img.src = full; img.alt = data(i, "alt");
        img.classList.add("is-loaded");
        if (loader) loader.hidden = true;
        if (dir && !REDUCED) {
          var cls = dir > 0 ? "mg-from-next" : "mg-from-prev";
          img.classList.remove("mg-from-next", "mg-from-prev");
          void img.offsetWidth; // restart animation
          img.classList.add(cls);
          img.addEventListener("animationend", function ae() {
            img.classList.remove(cls); img.removeEventListener("animationend", ae);
          }, { once: true });
        }
      };
      pre.onerror = function () { if (loader) loader.hidden = true; };
      if (ss) { pre.srcset = ss; pre.sizes = "100vw"; }
      pre.src = full;
    }

    /* Shared-element FLIP between a grid thumb rect and the lightbox image */
    function flip(startRect, dur) {
      if (!startRect) return;
      img.style.transition = "none";
      img.style.transform = "";
      var end = img.getBoundingClientRect();
      if (!end.width || !end.height) return;
      var sx = startRect.width / end.width;
      var sy = startRect.height / end.height;
      var dx = (startRect.left + startRect.width / 2) - (end.left + end.width / 2);
      var dy = (startRect.top + startRect.height / 2) - (end.top + end.height / 2);
      img.style.transformOrigin = "center center";
      img.style.transform = "translate(" + dx + "px," + dy + "px) scale(" + sx + "," + sy + ")";
      void img.offsetWidth; // reflow
      img.style.transition = "transform " + dur + "ms " + EASE;
      img.style.transform = "translate(0,0) scale(1,1)";
      img.addEventListener("transitionend", function te() {
        img.style.transition = ""; img.style.transform = "";
        img.removeEventListener("transitionend", te);
      }, { once: true });
    }

    function thumbRect(i) {
      var t = triggers[i].querySelector("img");
      return t ? t.getBoundingClientRect() : null;
    }
    function thumbSrc(i) {
      var t = triggers[i].querySelector("img");
      return t ? (t.currentSrc || t.src) : data(i, "full");
    }

    function open(i) {
      lastFocused = document.activeElement;
      current = i;
      lbOpen = true;
      // Back button / mobile back-gesture closes the lightbox instead of leaving the page; URL gets a shareable #foto-N
      if (!historyPushed) { try { history.pushState({ mgLb: 1 }, "", "#foto-" + (i + 1)); historyPushed = true; } catch (e) {} }
      setMeta(i);
      var startRect = thumbRect(i);

      box.hidden = false;
      box.setAttribute("aria-hidden", "false");
      document.body.classList.add("mg-scroll-lock");

      // Paint the (cached) thumb instantly so the FLIP has a real image
      img.classList.remove("is-loaded");
      img.removeAttribute("srcset");
      img.src = thumbSrc(i);
      img.alt = data(i, "alt");

      var begin = function () {
        img.classList.add("is-loaded");
        if (loader) loader.hidden = true;
        box.classList.add("is-open");
        if (!REDUCED) flip(startRect, 520);
        showFull(i, false);    // upgrade to full-res in place
        var list = activeList();
        var pos = list.indexOf(i); if (pos < 0) pos = 0;
        preload(list[(pos + 1) % list.length]);
        preload(list[(pos - 1 + list.length) % list.length]);
      };
      if (img.complete && img.naturalWidth) requestAnimationFrame(begin);
      else { img.onload = function () { img.onload = null; requestAnimationFrame(begin); }; }

      var closeBtn = box.querySelector("[data-mg-close]");
      if (closeBtn) closeBtn.focus();
      document.addEventListener("keydown", onKey);
    }

    function finishClose() {
      box.hidden = true;
      img.removeAttribute("src");
      img.removeAttribute("srcset");
      img.style.transition = ""; img.style.transform = ""; img.style.transformOrigin = "";
      frame.style.transform = "";
      backdrop.style.opacity = "";
      box.removeEventListener("transitionend", finishClose);
    }

    function close(fromPop) {
      if (!lbOpen) return;
      lbOpen = false;
      if (document.fullscreenElement || document.webkitFullscreenElement) {
        (document.exitFullscreen || document.webkitExitFullscreen || function () {}).call(document);
      }
      if (box.classList.contains("is-zoomed")) resetZoom();
      var startRect = thumbRect(current);
      if (startRect && !REDUCED) {
        var end = img.getBoundingClientRect();
        if (end.width) {
          var sx = startRect.width / end.width, sy = startRect.height / end.height;
          var dx = (startRect.left + startRect.width / 2) - (end.left + end.width / 2);
          var dy = (startRect.top + startRect.height / 2) - (end.top + end.height / 2);
          img.style.transition = "transform 420ms " + EASE;
          img.style.transformOrigin = "center center";
          img.style.transform = "translate(" + dx + "px," + dy + "px) scale(" + sx + "," + sy + ")";
        }
      }
      box.classList.remove("is-open");
      box.setAttribute("aria-hidden", "true");
      document.body.classList.remove("mg-scroll-lock");
      document.removeEventListener("keydown", onKey);
      if (REDUCED) finishClose();
      else box.addEventListener("transitionend", finishClose);
      if (lastFocused && typeof lastFocused.focus === "function") lastFocused.focus();
      // Pop the history entry we added on open (unless this close WAS triggered by popstate)
      if (historyPushed) { historyPushed = false; if (fromPop !== true) { try { history.back(); } catch (e) {} } }
    }

    function step(dir) {
      if (box.classList.contains("is-zoomed")) resetZoom();
      var list = activeList();
      var pos = list.indexOf(current); if (pos < 0) pos = 0;
      var nextPos = (pos + dir + list.length) % list.length;
      current = list[nextPos];
      try { history.replaceState(history.state, "", "#foto-" + (current + 1)); } catch (e) {}
      setMeta(current);
      showFull(current, true, dir);
      preload(list[(nextPos + dir + list.length) % list.length]);
    }

    function onKey(e) {
      switch (e.key) {
        case "Escape": e.preventDefault(); close(); break;
        case "ArrowRight": e.preventDefault(); step(1); break;
        case "ArrowLeft": e.preventDefault(); step(-1); break;
        case "f": case "F": e.preventDefault(); toggleFullscreen(); break;
        case "z": case "Z": e.preventDefault(); toggleZoom(); break;
        case "Tab": trapFocus(e); break;
      }
    }
    function trapFocus(e) {
      var nodes = Array.prototype.slice.call(box.querySelectorAll(FOCUSABLE))
        .filter(function (n) { return n.offsetParent !== null && !n.hidden; });
      if (!nodes.length) return;
      var first = nodes[0], last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    }

    /* Zoom — works on desktop (click image) AND via the zoom button (all devices) */
    box.classList.add("is-zoomable");
    function setOrigin(cx, cy) {
      var r = img.getBoundingClientRect();
      var ox = Math.max(0, Math.min(100, ((cx - r.left) / r.width) * 100));
      var oy = Math.max(0, Math.min(100, ((cy - r.top) / r.height) * 100));
      img.style.transformOrigin = ox + "% " + oy + "%";
    }
    function resetZoom() {
      box.classList.remove("is-zoomed");
      img.style.transformOrigin = "";
      img.style.removeProperty("--mg-zoom");
    }
    function curZoom() {
      var v = parseFloat(img.style.getPropertyValue("--mg-zoom"));
      if (!box.classList.contains("is-zoomed")) return 1;
      return v || 2;
    }
    function toggleZoom(cx, cy) {
      if (box.classList.contains("is-zoomed")) { resetZoom(); return; }
      if (cx != null) setOrigin(cx, cy);
      else { var r = img.getBoundingClientRect(); setOrigin(r.left + r.width / 2, r.top + r.height / 2); }
      box.classList.add("is-zoomed");
    }
    if (FINE) {
      img.addEventListener("click", function (e) { toggleZoom(e.clientX, e.clientY); });
      img.addEventListener("mousemove", function (e) { if (box.classList.contains("is-zoomed")) setOrigin(e.clientX, e.clientY); });
    }

    /* Fullscreen (immersive, great on mobile) */
    function fsEl() { return document.fullscreenElement || document.webkitFullscreenElement; }
    function fsSupported() { return !!(box.requestFullscreen || box.webkitRequestFullscreen); }
    function toggleFullscreen() {
      if (fsEl()) { (document.exitFullscreen || document.webkitExitFullscreen || function () {}).call(document); }
      else { var req = box.requestFullscreen || box.webkitRequestFullscreen; if (req) { var p = req.call(box); if (p && p.catch) p.catch(function () {}); } }
    }
    function onFsChange() { box.classList.toggle("is-fs", !!fsEl()); }
    document.addEventListener("fullscreenchange", onFsChange);
    document.addEventListener("webkitfullscreenchange", onFsChange);

    /* Touch: pinch = zoom · zoomed 1-finger = pan · swipe = nav · vertical drag = dismiss */
    var sx0 = 0, sy0 = 0, dragging = false, panning = false, axis = null;
    var pinching = false, pinchStartDist = 0, pinchStartScale = 1;
    function touchDist(t) { var dx = t[1].clientX - t[0].clientX, dy = t[1].clientY - t[0].clientY; return Math.sqrt(dx * dx + dy * dy); }
    function touchMidX(t) { return (t[0].clientX + t[1].clientX) / 2; }
    function touchMidY(t) { return (t[0].clientY + t[1].clientY) / 2; }
    var stage = box.querySelector("[data-mg-stage]") || box;
    stage.addEventListener("touchstart", function (e) {
      if (e.touches.length === 2) {
        pinching = true; dragging = false; panning = false;
        pinchStartDist = touchDist(e.touches) || 1;
        pinchStartScale = curZoom();
        setOrigin(touchMidX(e.touches), touchMidY(e.touches));
        img.style.transition = "none";
        return;
      }
      if (e.touches.length !== 1) return;
      sx0 = e.touches[0].clientX; sy0 = e.touches[0].clientY;
      if (box.classList.contains("is-zoomed")) { panning = true; return; }
      dragging = true; axis = null;
    }, { passive: true });
    stage.addEventListener("touchmove", function (e) {
      if (pinching && e.touches.length === 2) {
        var scale = pinchStartScale * (touchDist(e.touches) / pinchStartDist);
        scale = Math.max(1, Math.min(4, scale));
        setOrigin(touchMidX(e.touches), touchMidY(e.touches));
        img.style.setProperty("--mg-zoom", scale);
        box.classList.toggle("is-zoomed", scale > 1.02);
        return;
      }
      if (panning) { setOrigin(e.touches[0].clientX, e.touches[0].clientY); return; }
      if (!dragging) return;
      var dx = e.touches[0].clientX - sx0, dy = e.touches[0].clientY - sy0;
      if (!axis) axis = Math.abs(dx) > Math.abs(dy) ? "x" : "y";
      if (axis === "y") {
        box.classList.add("is-dragging");
        frame.style.transform = "translateY(" + dy + "px)";
        backdrop.style.opacity = Math.max(0, 1 - Math.abs(dy) / 420);
      }
    }, { passive: true });
    stage.addEventListener("touchend", function (e) {
      if (pinching) {
        if (e.touches.length < 2) {
          pinching = false;
          img.style.transition = "";
          if ((parseFloat(img.style.getPropertyValue("--mg-zoom")) || 1) <= 1.05) resetZoom();
        }
        return;
      }
      if (panning) { panning = false; return; }
      if (!dragging) return;
      dragging = false;
      var t = e.changedTouches[0];
      var dx = t.clientX - sx0, dy = t.clientY - sy0;
      box.classList.remove("is-dragging");
      if (axis === "y") {
        if (Math.abs(dy) > 120) { close(); }
        else { frame.style.transform = ""; backdrop.style.opacity = ""; }
      } else if (axis === "x") {
        if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
      }
    }, { passive: true });

    // Wire triggers + controls
    triggers.forEach(function (t, i) {
      t.addEventListener("click", function (e) { e.preventDefault(); open(i); });
    });
    box.querySelectorAll("[data-mg-close]").forEach(function (el) { el.addEventListener("click", function () { close(); }); });
    var prev = box.querySelector("[data-mg-prev]");
    var next = box.querySelector("[data-mg-next]");
    if (prev) prev.addEventListener("click", function () { step(-1); });
    if (next) next.addEventListener("click", function () { step(1); });
    var zoomBtn = box.querySelector("[data-mg-zoom]");
    if (zoomBtn) zoomBtn.addEventListener("click", function () { toggleZoom(); });
    var fsBtn = box.querySelector("[data-mg-fullscreen]");
    if (fsBtn) { if (!fsSupported()) fsBtn.style.display = "none"; else fsBtn.addEventListener("click", toggleFullscreen); }
    var shareBtn = box.querySelector("[data-mg-share]");
    if (shareBtn) shareBtn.addEventListener("click", function () { mgShare(location.href, data(current, "title") || "MIDOLO"); });

    // Browser back / mobile back-gesture closes the lightbox (instead of leaving the page)
    window.addEventListener("popstate", function () { if (lbOpen) close(true); });
    // Deep link: open #foto-N on initial load (shareable photo URLs)
    (function () {
      var m = (location.hash || "").match(/^#foto-(\d+)$/);
      if (m) { var n = parseInt(m[1], 10) - 1; if (n >= 0 && n < N) { setTimeout(function () { open(n); }, 60); } }
    })();
  }

  /* ---------- Back to top ------------------------------------------------- */
  function setupBackToTop(root) {
    var btn = root.querySelector("[data-mg-totop]");
    if (!btn) return;
    function onScroll() {
      var y = window.scrollY || window.pageYOffset || 0;
      btn.classList.toggle("is-on", y > (window.innerHeight || 800) * 0.8);
    }
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    btn.addEventListener("click", function () {
      window.scrollTo({ top: 0, behavior: REDUCED ? "auto" : "smooth" });
      replayReveal(root);
    });
  }

  /* ---------- Boot -------------------------------------------------------- */
  function boot() { document.querySelectorAll("[data-midolo-gallery]").forEach(init); }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", boot);
  } else { boot(); }
  document.addEventListener("shopify:section:load", function (e) {
    var el = e.target.querySelector("[data-midolo-gallery]");
    if (el) init(el);
  });
})();
